/**
 * The cadence picker's vocabulary <-> a 5-field cron expression, which is what
 * `JobSchedule` stores. Kept out of the component so it can be tested without
 * pulling the RTK store in.
 */
export type Frequency = "daily" | "weekly" | "monthly" | "quarterly"

export interface Cadence {
  frequency: Frequency
  time: string
  dayOfWeek: number
}

export const DEFAULT_CADENCE: Cadence = {
  frequency: "weekly",
  time: "09:00",
  dayOfWeek: 1,
}

export function toCron({ frequency, time, dayOfWeek }: Cadence): string {
  const [hour, minute] = time.split(":").map(Number)
  switch (frequency) {
    case "weekly":
      return `${minute} ${hour} * * ${dayOfWeek}`
    case "monthly":
      return `${minute} ${hour} 1 * *`
    // First day of each calendar quarter.
    case "quarterly":
      return `${minute} ${hour} 1 1,4,7,10 *`
    default:
      return `${minute} ${hour} * * *`
  }
}

/**
 * Best-effort inverse of `toCron`, for seeding the form from a saved schedule.
 * Anything this UI didn't write (a hand-crafted cron) falls back to the default
 * cadence rather than rendering a nonsense selection — and saving then rewrites
 * it to a cron the picker can express.
 */
export function fromCron(cron: string): Cadence {
  const parts = cron.trim().split(/\s+/)
  if (parts.length !== 5) return DEFAULT_CADENCE
  const [minute, hour, dayOfMonth, month, dayOfWeek] = parts
  if (!/^\d{1,2}$/.test(minute) || !/^\d{1,2}$/.test(hour)) {
    return DEFAULT_CADENCE
  }

  const time = `${hour.padStart(2, "0")}:${minute.padStart(2, "0")}`
  // Only the exact quarter-month set is "quarterly" — a month-specific cron this
  // picker never wrote (e.g. `0 9 1 3 *`, March only) is not quarterly and must
  // fall through to the default rather than be mislabelled as one.
  if (month === "1,4,7,10") {
    return { frequency: "quarterly", time, dayOfWeek: 1 }
  }
  if (month !== "*") return DEFAULT_CADENCE
  if (dayOfMonth !== "*") return { frequency: "monthly", time, dayOfWeek: 1 }
  if (dayOfWeek !== "*" && /^\d$/.test(dayOfWeek)) {
    return { frequency: "weekly", time, dayOfWeek: Number(dayOfWeek) }
  }
  return { frequency: "daily", time, dayOfWeek: 1 }
}
